/**
 * mySchoolCafe meal palette — single source of truth for JS consumers.
 * Sass mirror: src/assets/sass/_mealPalette.scss (keep values in sync)
 *
 * Intent: warm, wholesome week rhythm; no reds in the day identity set.
 * Mini-calendar DOW strip uses neutral slate chrome so it never fights a day color.
 */

import Moment from 'moment-timezone';

export const MEAL_DAY_COLORS = {
  Sunday: '#3d7ea6',
  Monday: '#1d9a86',
  Tuesday: '#6a9e3e',
  Wednesday: '#c88214',
  Thursday: '#7c5cbf',
  Friday: '#b85c6e',
  Saturday: '#3d7ea6',
};

/** Tear-off calendar DOW header — paper binding, not a weekday color */
export const MEAL_CALENDAR_CHROME_DOW = '#2e3d4f';

/** Calendar titles, nav controls, print affordances */
export const MEAL_BRAND_PRIMARY = '#2a7f9e';

/** Sidebar picker legend */
export const MEAL_STATUS_AVAILABLE = '#2ea8b8';
export const MEAL_STATUS_ORDERED = '#6fa848';

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export function mealDayColor(weekdayName) {
  return MEAL_DAY_COLORS[weekdayName] || MEAL_DAY_COLORS.Monday;
}

/**
 * Weekday name for a menu/order date — +12h matches MealsPage menu matching
 * and avoids UTC-midnight shifting the day in US timezones.
 */
export function menuDateWeekdayName(menuDate, tz) {
  let m = Moment(menuDate);
  if (tz) {
    m = m.tz(tz);
  }
  return m.add(12, 'hours').format('dddd');
}

export function mealDayColorFromMenuDate(menuDate, tz) {
  return mealDayColor(menuDateWeekdayName(menuDate, tz));
}

/** Weekday for a Y-M-D grid cell in the monthly calendar */
export function calendarCellWeekdayName(year, month, dayOfMonth, tz) {
  const monthPadded = String(month).padStart(2, '0');
  const dayPadded = String(dayOfMonth).padStart(2, '0');
  let m = Moment(`${year}-${monthPadded}-${dayPadded}`, 'YYYY-MM-DD');
  if (tz) {
    m = m.tz(tz, true);
  }
  return m.format('dddd');
}

export function mealDayColorByWeekIndex(emptyDays, index) {
  return MEAL_DAY_COLORS[WEEKDAY_NAMES[(emptyDays + index) % 7]];
}

/** CSS custom properties — set on .msc-meals-page or workspace roots */
export function mealPaletteCssVars() {
  return {
    '--meal-day-sunday': MEAL_DAY_COLORS.Sunday,
    '--meal-day-monday': MEAL_DAY_COLORS.Monday,
    '--meal-day-tuesday': MEAL_DAY_COLORS.Tuesday,
    '--meal-day-wednesday': MEAL_DAY_COLORS.Wednesday,
    '--meal-day-thursday': MEAL_DAY_COLORS.Thursday,
    '--meal-day-friday': MEAL_DAY_COLORS.Friday,
    '--meal-day-saturday': MEAL_DAY_COLORS.Saturday,
    '--meal-chrome-dow': MEAL_CALENDAR_CHROME_DOW,
    '--meal-brand': MEAL_BRAND_PRIMARY,
    '--meal-status-available': MEAL_STATUS_AVAILABLE,
    '--meal-status-ordered': MEAL_STATUS_ORDERED,
  };
}
